import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { INVITATION_ENTRANCE } from "../../store/services/invitations/dto/invitations.dto";
import { InvitationEntrancePickerContainer } from "./styles";

const ENTRANCE_OPTIONS = [
	{ id: INVITATION_ENTRANCE.NAMES, label: "Names" },
	{ id: INVITATION_ENTRANCE.ENVELOPE, label: "Envelope" },
	{ id: INVITATION_ENTRANCE.RINGS, label: "Rings" },
	{ id: INVITATION_ENTRANCE.CURTAIN, label: "Curtain" },
	{ id: INVITATION_ENTRANCE.PALACE_DOORS, label: "Palace" }
];

const SCROLL_STEP = 110;

const InvitationEntrancePicker = ({
	selectedEntrance,
	onEntrancePick
}: {
	selectedEntrance: INVITATION_ENTRANCE;
	onEntrancePick: (entrance: INVITATION_ENTRANCE) => void;
}) => {
	const scrollerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 1);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;
		updateArrows();
		const observer = new ResizeObserver(updateArrows);
		observer.observe(el);
		return () => observer.disconnect();
	}, [updateArrows]);

	const scrollByStep = (direction: 1 | -1) => {
		scrollerRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: "smooth" });
	};

	return (
		<InvitationEntrancePickerContainer p={0.5}>
			<button
				type={"button"}
				className={`scroll-arrow ${canScrollLeft ? "visible" : ""}`}
				aria-label={"Show previous entrances"}
				tabIndex={canScrollLeft ? 0 : -1}
				onClick={() => scrollByStep(-1)}
			>
				<ChevronLeft />
			</button>
			<div className={"scroller"} ref={scrollerRef} onScroll={updateArrows}>
				{ENTRANCE_OPTIONS.map(option => (
					<Box
						key={option.id}
						className={`entrance-option ${selectedEntrance === option.id ? "outlined" : ""}`}
						onClick={() => onEntrancePick(option.id)}
					>
						{option.label}
					</Box>
				))}
			</div>
			<button
				type={"button"}
				className={`scroll-arrow ${canScrollRight ? "visible" : ""}`}
				aria-label={"Show more entrances"}
				tabIndex={canScrollRight ? 0 : -1}
				onClick={() => scrollByStep(1)}
			>
				<ChevronRight />
			</button>
		</InvitationEntrancePickerContainer>
	);
};

export default InvitationEntrancePicker;
